import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import ProtectedRoute from "./components/ProtectedRoute";
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Onboarding from "./pages/Onboarding";
import Dashboard from "./pages/Dashboard";
import Labs from "./pages/Labs";
import LabWorkspace from "./pages/LabWorkspace";
import ProgressPage from "./pages/Progress";
import GoogleCallback from "./pages/GoogleCallback";
import Pricing from "./pages/Pricing";
import BillingSuccess from "./pages/BillingSuccess";
import VerifyEmail from "./pages/VerifyEmail";
import AdminLabsHealth from "./pages/AdminLabsHealth";
import { Toaster } from "sonner";
import "@/App.css";

function AuthGate({ children }) {
  // If already logged in and verified, redirect /login and /signup to dashboard.
  // Unverified users are intentionally allowed to land on /login or /signup
  // so flows like "signup → /verify-email?reclaimed=1" can take effect (the
  // explicit nav from Signup.jsx must win over a generic dashboard redirect).
  const { user } = useAuth();
  if (user && user !== false && user.email_verified) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route
        path="/login"
        element={
          <AuthGate>
            <Login />
          </AuthGate>
        }
      />
      <Route
        path="/signup"
        element={
          <AuthGate>
            <Signup />
          </AuthGate>
        }
      />
      <Route
        path="/onboarding"
        element={
          <ProtectedRoute requireOnboarded={false}>
            <Onboarding />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/labs"
        element={
          <ProtectedRoute>
            <Labs />
          </ProtectedRoute>
        }
      />
      <Route
        path="/labs/:labId"
        element={
          <ProtectedRoute>
            <LabWorkspace />
          </ProtectedRoute>
        }
      />
      <Route
        path="/progress"
        element={
          <ProtectedRoute>
            <ProgressPage />
          </ProtectedRoute>
        }
      />
      <Route path="/auth/google/callback" element={<GoogleCallback />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route
        path="/admin/labs"
        element={
          <ProtectedRoute>
            <AdminLabsHealth />
          </ProtectedRoute>
        }
      />
      <Route
        path="/pricing"
        element={
          <ProtectedRoute>
            <Pricing />
          </ProtectedRoute>
        }
      />
      <Route
        path="/billing/success"
        element={
          <ProtectedRoute>
            <BillingSuccess />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ThemeProvider>
          <AuthProvider>
            <AppRoutes />
            <Toaster theme="dark" position="bottom-right" />
          </AuthProvider>
        </ThemeProvider>
      </BrowserRouter>
    </div>
  );
}
